import { toolsMetadata, ToolMeta } from './toolsMetadata';

export interface UnifiedToolLike {
  id: string;
  name: string;
  description?: string;
  triggers?: string[];
  route?: string;
}

export type UnifiedActionType = 'cta' | 'draft';

export interface UnifiedAction {
  actionType: UnifiedActionType;
  toolId: string;
  toolName: string;
  score: number;
  text: string; // assistant text to show
  ctaLabel?: string;
  route?: string;
  payload?: Record<string, any>;
}

const STOPWORDS = new Set([
  'the','a','an','to','for','of','and','or','in','my','our','your','with','on','at','by','from','about','into','over','after'
]);

const SYNONYMS: Record<string, string[]> = {
  website: ['site', 'web site', 'homepage', 'landing page', 'pages'],
  build: ['create', 'make', 'generate', 'setup', 'set up', 'produce'],
  generator: ['builder', 'maker', 'creator'],
  image: ['picture', 'photo', 'graphic', 'visual'],
  video: ['clip', 'short', 'reel'],
  social: ['facebook', 'linkedin', 'twitter', 'x', 'instagram', 'threads'],
  seo: ['search', 'rank', 'ranking', 'organic'],
  email: ['nurture', 'drip', 'newsletter'],
  leads: ['lead', 'pipeline', 'mql', 'prospect'],
};

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .filter(w => !STOPWORDS.has(w));
}

function expandTriggers(base: string[]): string[] {
  const out = new Set<string>();
  for (const trig of base) {
    out.add(trig.toLowerCase());
    const parts = tokenize(trig);
    for (const p of parts) {
      if (SYNONYMS[p]) {
        for (const s of SYNONYMS[p]) out.add(s);
      }
    }
  }
  return Array.from(out);
}

function keywordScore(text: string, triggers: string[], tool?: { name?: string; description?: string }): number {
  const t = text.toLowerCase();
  let score = 0;
  const expanded = expandTriggers(triggers);

  if (tool?.name) {
    const nameTokens = tokenize(tool.name).filter(w => !STOPWORDS.has(w));
    for (const n of nameTokens) expanded.push(n);
  }

  const matched = new Set<string>();
  for (const kRaw of expanded) {
    const k = kRaw.toLowerCase();
    if (!k) continue;
    if (k.includes(' ')) {
      if (t.includes(k)) { score += 2; matched.add(k); }
    } else {
      if (new RegExp(`\\b${k}\\b`, 'i').test(text)) { score += 1; matched.add(k); }
    }
  }

  if (matched.size >= 2) score += 0.5;
  if (matched.size >= 3) score += 0.5;

  if (tool?.name && t.includes(tool.name.toLowerCase())) score += 0.5;

  if (tool?.description) {
    const desc = tool.description.toLowerCase();
    matched.forEach((m) => {
      if (desc.includes(m)) { score += 0.2; }
    });
  }

  return score;
}

export function evaluateIntent(message: string, tools: UnifiedToolLike[]): UnifiedAction | null {
  if (!message || typeof message !== 'string') return null;
  const lower = message.toLowerCase();

  // Heuristic: Website builder => CTA
  if (/(build|create|make|setup|set up).*(website|site|homepage|landing\s?page)/i.test(lower)) {
    const websiteTool = tools.find(t => t.id === 'marketing.ai_website');
    if (websiteTool) {
      return {
        actionType: 'cta',
        toolId: websiteTool.id,
        toolName: websiteTool.name,
        score: 99,
        text: `I can open ${websiteTool.name} to help with that.`,
        ctaLabel: websiteTool.route ? `Open ${websiteTool.name}` : `Use ${websiteTool.name}`,
        route: websiteTool.route,
      };
    }
  }

  // Heuristic: Social post => Draft by default with platform/topic extraction
  if (/(create|write|draft|compose|make)\s+.*\b(post|linkedin|social|tweet|twitter|instagram|threads|x)\b/i.test(lower) || /\bpost\b\s+.*\babout\b/i.test(lower)) {
    const postTool = tools.find(t => t.id === 'marketing.social_posts');
    if (postTool) {
      const platform = /(linkedin|ln)/i.test(lower)
        ? 'linkedin'
        : /(twitter|tweet|x)/i.test(lower)
        ? 'twitter'
        : /(instagram)/i.test(lower)
        ? 'instagram'
        : 'generic';
      // Extract rough topic: remove directive verbs and platform words
      let topic = message
        .replace(/^(please|can you|could you)\s*/i, '')
        .replace(/(create|write|draft|compose|make)\s+(a\s+)?/i, '')
        .replace(/\b(linkedin|ln|post|tweet|twitter|instagram|social)\b/gi, '')
        .replace(/\s+/g, ' ')
        .trim();
      const payload: Record<string, any> = { platform, topic };
      return {
        actionType: 'draft',
        toolId: postTool.id,
        toolName: postTool.name,
        score: 98.5,
        text: `I'll draft a ${platform === 'generic' ? 'social' : platform} post for you.`,
        route: postTool.route,
        payload,
      };
    }
  }

  // Heuristic: email => CTA to email sender
  if (/(send|compose|write)\s+(an\s+)?email(\s+to\s+|\s+)/i.test(lower) && !/\b(post|linkedin|social|tweet|twitter|instagram|threads|x)\b/i.test(lower)) {
    const emailTool = tools.find(t => t.id === 'communications.email.send');
    if (emailTool) {
      return {
        actionType: 'cta',
        toolId: emailTool.id,
        toolName: emailTool.name,
        score: 98,
        text: `I can send an email using ${emailTool.name}.`,
        ctaLabel: emailTool.route ? `Open ${emailTool.name}` : `Use ${emailTool.name}`,
        route: emailTool.route,
      };
    }
  }

  // Scored fallback across tools
  const scores = tools.map((tool) => ({
    tool,
    score: keywordScore(message, tool.triggers || [], tool),
  }));

  const best = scores.sort((a, b) => b.score - a.score)[0];
  if (!best) return null;

  const len = tokenize(message).length;
  const threshold = len > 8 ? 1.6 : 1.8;
  if (best.score < threshold) return null;

  const ctaLabel = best.tool.route ? `Open ${best.tool.name}` : `Use ${best.tool.name}`;
  return {
    actionType: 'cta',
    toolId: best.tool.id,
    toolName: best.tool.name,
    score: best.score,
    text: `I can open ${best.tool.name} to help with that.`,
    ctaLabel,
    route: best.tool.route,
  };
}

// Compatibility wrapper for environments already using shared toolsMetadata
export function evaluateLocalActions(message: string): UnifiedAction | null {
  const tools: UnifiedToolLike[] = toolsMetadata.map((t: ToolMeta) => ({
    id: t.id,
    name: t.name,
    description: t.description,
    triggers: t.triggers,
    route: t.route,
  }));
  return evaluateIntent(message, tools);
}
